import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ArrowUpRight } from "lucide-react";
import { useContent, useProjects } from "@/lib/content";
import { FadeUp, Eyebrow } from "@/components/Motion";
import ProjectDialog from "@/components/ProjectDialog";
import { imgSrc } from "@/lib/api";

export default function OurWork() {
  const content = useContent();
  const { data: projects } = useProjects();
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  const categories = useMemo(() => {
    const set = new Set((projects || []).map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [projects]);

  const filtered = filter === "All" ? projects || [] : (projects || []).filter((p) => p.category === filter);

  if (!content) return <div className="min-h-screen bg-ivory" />;
  const wi = content.work_intro;
  const ba = content.before_after;

  return (
    <main className="pt-[72px]">
      {/* header */}
      <section className="border-b border-charcoal/10 py-16 sm:py-24" data-testid="work-header">
        <div className="wrap">
          <Eyebrow className="mb-6">{wi.eyebrow}</Eyebrow>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-12">
              <FadeUp delay={0.05}>
                <h1 className="font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">{wi.title}</h1>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* filters + grid */}
      <section className="py-14 sm:py-20" data-testid="work-gallery">
        <div className="wrap">
          <FadeUp className="flex flex-wrap gap-2.5" y={16}>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid="work-filter-tabs"
                data-testid-value={c}
                className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                  filter === c
                    ? "border-charcoal bg-charcoal text-ivory"
                    : "border-charcoal/15 bg-paper text-charcoal/60 hover:border-charcoal/40 hover:text-charcoal"
                }`}
              >
                {c}
              </button>
            ))}
          </FadeUp>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <FadeUp key={p.id} delay={(i % 3) * 0.07}>
                <button onClick={() => setActive(p)} className="group block w-full text-left" data-testid={`work-project-card-${i}`}>
                  <div className="photo-frame aspect-[4/5] rounded-lg">
                    <img src={imgSrc(p.cover_image)} alt={p.title} loading="lazy" />
                    <span className="pointer-events-none absolute bottom-4 left-4 z-[2] rounded-full bg-charcoal/70 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-ivory opacity-0 backdrop-blur-sm transition-opacity duration-500 group-hover:opacity-100">
                      View project
                    </span>
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-xl text-charcoal sm:text-2xl">{p.title}</h3>
                      <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/45">
                        <span className="text-gold">{p.category}</span>
                        {p.location && <span className="inline-flex items-center gap-1"><MapPin size={10} />{p.location}</span>}
                      </p>
                    </div>
                    <ArrowUpRight size={16} className="mt-1.5 text-charcoal/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
                  </div>
                </button>
              </FadeUp>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="py-20 text-center font-serif text-2xl italic text-charcoal/40">
              New projects are being photographed. Check back soon.
            </p>
          )}

          {filtered.length > 0 && (
            <FadeUp className="mt-24 flex flex-col items-center gap-5 text-center">
              <span className="h-px w-12 bg-gold/50" />
              <p className="font-serif text-2xl italic text-charcoal sm:text-3xl">More projects coming soon.</p>
            </FadeUp>
          )}
        </div>
      </section>

      {/* cta */}
      <section className="py-20 sm:py-28">
        <div className="wrap text-center">
          <FadeUp>
            <h2 className="mx-auto max-w-2xl font-serif text-3xl leading-tight text-charcoal sm:text-5xl">
              Your home could be the next one on this page.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link to="/contact" className="btn-dark" data-testid="work-cta-quote">Start your project <ArrowRight size={16} /></Link>
              <a href="tel:+61456492849" className="btn-ghost">Call 0456 492 849</a>
            </div>
          </FadeUp>
        </div>
      </section>

      <ProjectDialog project={active} open={!!active} onClose={() => setActive(null)} />
    </main>
  );
}
